import { h } from 'preact';
import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'preact/hooks';
import type { ContextFullness } from '@voice-assistant/shared';
import { computeDrawerBox } from '../utils/drawerBox';
import { modelShortName as registryMissShortName, modelBadge } from '../utils/modelHelpers';
import { intentFriendlyLabel } from '../utils/intentLabel';

/** Capability flags surfaced from the registry join (Phase 3). Verbatim field names. */
interface TurnMetaCapabilities {
  supportsEffort?: boolean;
  supports1MContext?: boolean;
  supportsAdaptiveThinking?: boolean;
  vision?: boolean;
  toolCapable?: boolean;
  [key: string]: unknown;
}

interface TurnMeta {
  model: string | null;
  /** Registry displayName — raw id on a registry miss (Phase 3). */
  displayName: string | null;
  /** Verbatim registry capabilities jsonb (Phase 3). */
  capabilities: TurnMetaCapabilities | null;
  costUsd: number | null;
  costSource: 'api' | 'subscription' | 'unknown';
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  cacheCreationTokens: number | null;
  intentType: string | null;
  classifierModel: string | null;
  classifierCost: number | null;
  effortLevel: string | null;
  timestamp: string | null;
  sessionModel: string | null;
  contextWindow: number | null;
  /** Ledger-resolved occupancy union (context-fullness Phase 0b) — 'pending'
   *  when the session never persisted an actuatable reading. */
  contextFullness?: ContextFullness | null;
  /** Harness Inspector (Phase 1c): the cascade model this turn's intent resolved to + its
   *  originating config layer. `seed` = the static default (no runtime override). null when
   *  the turn's intent has no cascade binding (e.g. session_mgmt). */
  harnessResolution?: {
    key: string;
    model: string;
    layer: 'user' | 'profile' | 'global' | 'seed';
  } | null;
}

interface TurnMetaDrawerProps {
  open: boolean;
  onClose: () => void;
  messageId: string | null;
  anchorY: number;
  initialData: { model?: string; costUsd?: number; timestamp?: string };
}

function formatTokens(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/**
 * Compact model label for the drawer (Phase 3) — registry = single source of truth.
 * Prefers the meta's registry `displayName`; on a registry miss (null) falls back to the
 * id-based parser so the drawer degrades gracefully instead of going blank.
 */
function resolveModelLabel(model: string | null, displayName: string | null): string {
  if (!model) return '—';
  if (model === 'cascade') return 'Cascade Agent';
  if (displayName) return displayName.replace(/^claude\s+/i, '');
  return registryMissShortName(model, false);
}

/** Provider tint class for the model badge — sourced from the shared modelHelpers map (Phase 3). */
function modelCls(model: string | null): string {
  if (!model) return '';
  return modelBadge(model, false).cls;
}

export function TurnMetaDrawer({ open, onClose, messageId, anchorY, initialData }: TurnMetaDrawerProps) {
  const [meta, setMeta] = useState<TurnMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  // Reactive box geometry — recomputed after the panel measures its real content height.
  const [box, setBox] = useState<{ top: number; maxHeight: number }>(() => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    return computeDrawerBox(anchorY, vh, vh); // optimistic full-height until measured
  });

  useEffect(() => {
    if (!open || !messageId) { setMeta(null); return; }
    setLoading(true);
    fetch(`/api/sessions/messages/${messageId}/meta`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setMeta(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, messageId]);

  // Measure the panel's actual content height and re-derive a box that is ALWAYS fully on-screen
  // (flips upward for bottom-anchored badges; max-height capped at vh - margin). Re-runs when the
  // content changes (loading → meta) so a tall cascade turn never overflows below the fold.
  useLayoutEffect(() => {
    if (!open) return;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const contentH = panelRef.current?.scrollHeight ?? vh;
    setBox(computeDrawerBox(anchorY, vh, contentH));
  }, [open, anchorY, meta, loading]);

  // Browser back button dismiss
  useEffect(() => {
    if (!open) return;
    history.pushState({ turnMeta: true }, '');
    const onPop = (_e: PopStateEvent) => { onClose(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open]);

  // Click outside dismiss
  const handleBackdrop = useCallback((e: MouseEvent | TouchEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  }, [onClose]);

  // Swipe right dismiss
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 60) onClose();
  }, [onClose]);

  if (!open) return null;

  // Per-turn (historical) model — what actually ran THIS turn. Distinct from the
  // current-session model/effort (which can drift after the turn was recorded).
  const turnModel = meta?.model ?? initialData.model ?? null;
  const turnDisplayName = meta?.displayName ?? null;
  const costUsd = meta?.costUsd ?? initialData.costUsd ?? null;
  const ts = meta?.timestamp ?? initialData.timestamp ?? null;
  const sessionModel = meta?.sessionModel ?? null;
  const isCascadeSession = sessionModel === 'cascade';
  const turnModelDiffersFromSession = isCascadeSession && turnModel && turnModel !== 'cascade';

  const caps = meta?.capabilities ?? null;
  const capList: string[] = [];
  if (caps) {
    if (caps.supports1MContext) capList.push('1M context');
    if (caps.supportsEffort) capList.push('effort');
    if (caps.supportsAdaptiveThinking) capList.push('thinking');
    if (caps.vision) capList.push('vision');
    if (caps.toolCapable) capList.push('tools');
  }

  // Occupancy union (context-fullness Phase 0b) — ledger-resolved SERVER-side
  // from the persisted actuatable pair; the drawer renders it and never
  // recomputes a pct from billing tokens (SE-09).
  const fullness = meta?.contextFullness ?? null;

  return (
    <div class="turn-meta-backdrop" onClick={handleBackdrop}>
      <div
        ref={panelRef}
        class={`turn-meta-drawer ${open ? 'turn-meta-drawer--open' : ''}`}
        style={{ top: `${box.top}px`, maxHeight: `${box.maxHeight}px` }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Session mode */}
        <div class="turn-meta-section">
          <div class="turn-meta-section-title">Session</div>
          <div class="turn-meta-row">
            {isCascadeSession ? (
              <span class="turn-meta-session-badge turn-meta-session-badge--cascade">⚡ Cascade Agent</span>
            ) : (
              <span class={`turn-meta-session-badge turn-meta-session-badge--fixed ${modelCls(sessionModel)}`}>
                Fixed: {resolveModelLabel(sessionModel, isCascadeSession ? null : turnDisplayName)}
              </span>
            )}
          </div>
          {/* Actual model this turn — only relevant in cascade (session model is always 'cascade') */}
          {turnModelDiffersFromSession && (
            <div class="turn-meta-row">
              <span class="turn-meta-label">This turn</span>
              <span class={`turn-meta-model-badge ${modelCls(turnModel)}`}>{resolveModelLabel(turnModel, turnDisplayName)}</span>
              <span class="turn-meta-model-id">{turnModel}</span>
            </div>
          )}
          {!isCascadeSession && (
            <div class="turn-meta-row">
              <span class="turn-meta-model-id">{turnModel || '—'}</span>
            </div>
          )}
          {capList.length > 0 && (
            <div class="turn-meta-row">
              <span class="turn-meta-label">Capabilities</span>
              <span class="turn-meta-value">{capList.join(' · ')}</span>
            </div>
          )}
        </div>

        {/* Routing */}
        <div class="turn-meta-section">
          <div class="turn-meta-section-title">Routing</div>
          <div class="turn-meta-row">
            <span class="turn-meta-label">Intent</span>
            <span class="turn-meta-value" title={meta?.intentType || undefined}>{loading ? '…' : (intentFriendlyLabel(meta?.intentType) || '—')}</span>
          </div>
          <div class="turn-meta-row">
            <span class="turn-meta-label">Classifier</span>
            <span class="turn-meta-value">
              {loading ? '…' : (
                meta?.classifierModel
                  ? `${resolveModelLabel(meta.classifierModel, null)}${meta.classifierCost != null ? ` · $${meta.classifierCost.toFixed(4)}` : ''}`
                  : 'regex fallback'
              )}
            </span>
          </div>
          {meta?.harnessResolution && (
            <div class="turn-meta-row">
              <span class="turn-meta-label">Resolved via</span>
              <span class="turn-meta-value" title={meta.harnessResolution.key}>
                {`${meta.harnessResolution.layer} · ${resolveModelLabel(meta.harnessResolution.model, null)}`}
              </span>
            </div>
          )}
        </div>

        {/* Tokens */}
        <div class="turn-meta-section">
          <div class="turn-meta-section-title">Tokens</div>
          <div class="turn-meta-row">
            <span class="turn-meta-label">Input</span>
            <span class="turn-meta-value">{loading ? '…' : formatTokens(meta?.inputTokens ?? null)}</span>
          </div>
          <div class="turn-meta-row">
            <span class="turn-meta-label">Output</span>
            <span class="turn-meta-value">{loading ? '…' : formatTokens(meta?.outputTokens ?? null)}</span>
          </div>
          <div class="turn-meta-row">
            <span class="turn-meta-label">Cache read</span>
            <span class="turn-meta-value">{loading ? '…' : formatTokens(meta?.cacheReadTokens ?? null)}</span>
          </div>
        </div>

        {/* Cost */}
        <div class="turn-meta-section">
          <div class="turn-meta-section-title">Cost</div>
          <div class="turn-meta-row">
            {costUsd != null && costUsd > 0 ? (
              <span class="turn-meta-cost">
                ${costUsd < 0.01 ? costUsd.toFixed(4) : costUsd.toFixed(2)}
              </span>
            ) : (
              <span class="turn-meta-cost-label">
                {meta?.costSource === 'subscription'
                  ? 'Subscription — no billing data'
                  : '—'}
              </span>
            )}
          </div>
        </div>

        {/* Context — the occupancy union: measured NN% · projected ~NN% ·
            unknown-window NNk / ? · unmetered — (with hover reason). */}
        {fullness && fullness.kind !== 'pending' && (
          <div class="turn-meta-section">
            <div class="turn-meta-section-title">Context</div>
            <div class="turn-meta-row">
              <span
                class="turn-meta-value"
                title={fullness.kind === 'unmetered'
                  ? (fullness.reason === 'stale-anchor'
                    ? 'Context not metered — the last reading went stale'
                    : 'Context not metered — no usage reported by the provider')
                  : undefined}
              >
                {(fullness.kind === 'measured' || fullness.kind === 'projected') &&
                  `${fullness.kind === 'projected' ? '~' : ''}${formatTokens(fullness.contextTokens)} / ${formatTokens(fullness.contextWindow)}`}
                {fullness.kind === 'unknown-window' && `${formatTokens(fullness.contextTokens)} / ?`}
                {fullness.kind === 'unmetered' && '—'}
              </span>
            </div>
            {(fullness.kind === 'measured' || fullness.kind === 'projected') && (
              <div class="turn-meta-context-bar">
                <div class="turn-meta-context-fill" style={{ width: `${Math.min(fullness.pct, 100)}%` }} />
              </div>
            )}
          </div>
        )}

        {/* Effort — session-level (chat_message has no per-turn effort column; labeled as such). */}
        {meta?.effortLevel && (
          <div class="turn-meta-section">
            <div class="turn-meta-section-title">Effort (session)</div>
            <div class="turn-meta-row">
              <span class="turn-meta-value">{meta.effortLevel}</span>
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div class="turn-meta-section">
          <div class="turn-meta-section-title">Time</div>
          <div class="turn-meta-row">
            <span class="turn-meta-value">{ts ? new Date(ts).toLocaleString() : '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
